import { useEffect, useState, Suspense } from "react";
import { useTranslation } from "react-i18next";
import Nav from "@/components/Header/Nav/Nav";
import Footer from "@/components/Footer/Footer";
import FaqBlock from "@/components/FAQ/FAQBlock";
import { Helmet } from "react-helmet-async";

interface FaqSection {
  title: string;
  data: any[];
}

const FAQ = () => {
  const { t, i18n } = useTranslation("pages");
  const [faqSections, setFaqSections] = useState<FaqSection[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [visibleSections, setVisibleSections] = useState<number>(2);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Функція для імпорту FAQ секції з підтримкою Vite
  const importFaqSection = async (sectionNum: number, lang: string) => {
    // Vite вимагає явного glob pattern для dynamic imports
    const modules = import.meta.glob("../data/faq/faqData*.ts");
    const key = `../data/faq/faqData${sectionNum}.${lang}.ts`;

    if (modules[key]) {
      return await modules[key]();
    }

    throw new Error(`FAQ section ${sectionNum} for language ${lang} not found`);
  };

  // Функція для динамічного завантаження секцій з урахуванням мови
  const loadFaqSections = async (sectionsToLoad: number) => {
    setIsLoading(true);
    const loadedSections: FaqSection[] = [];
    const lang = i18n.language; // de, pl, ua, cz

    try {
      for (let i = 1; i <= sectionsToLoad; i++) {
        try {
          const module: any = await importFaqSection(i, lang);
          loadedSections.push({
            title: module[`faqTitle${i}`],
            data: module[`faqData${i}`],
          });
        } catch (error) {
          console.error(`FAQ секція ${i} для мови ${lang} не знайдена`, error);
          // Fallback на німецьку, якщо переклад не знайдено
          if (lang !== "de") {
            try {
              const fallbackModule: any = await importFaqSection(i, "de");
              loadedSections.push({
                title: fallbackModule[`faqTitle${i}`],
                data: fallbackModule[`faqData${i}`],
              });
            } catch (fallbackError) {
              console.error(
                `FAQ секція ${i} не знайдена навіть в DE`,
                fallbackError
              );
            }
          }
        }
      }

      setFaqSections(loadedSections);
    } catch (error) {
      console.error("Помилка завантаження секцій FAQ:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFaqSections(visibleSections);
  }, [visibleSections, i18n.language]);

  const handleScroll = () => {
    if (
      window.innerHeight + document.documentElement.scrollTop >=
      document.documentElement.offsetHeight - 200
    ) {
      if (visibleSections < 7 && !isLoading) {
        setVisibleSections((prev) => Math.min(prev + 1, 7));
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visibleSections, isLoading]);

  return (
    <div>
      <Helmet>
        <title>{t("faq.meta.title")}</title>
        <meta name="description" content={t("faq.meta.description")} />
      </Helmet>

      <Nav />

      <div className="sectionPage container">
        <div className="sectionTitle sideP88 borderNo">
          <h1>{t("faq.title")}</h1>
        </div>
      </div>

      <Suspense fallback={<div>{t("faq.loadingSection")}</div>}>
        {faqSections.map((section, index) => (
          <div key={index}>
            <FaqBlock
              faqData={section.data}
              title={section.title}
              showNumbers={true}
            />
          </div>
        ))}
      </Suspense>

      {isLoading && <div>{t("common.loading", { ns: "common" })}</div>}

      <Footer />
    </div>
  );
};

export default FAQ;
